/* eslint-disable no-restricted-imports */
// "Registration questions" on the event form: the questions the website asks
// each participant at checkout (and Family Profile asks on a portal add).
// Saved on their own, with one PUT of the whole definition, separately from
// the event details. Contract: zebra-portal-server docs/events-custom-fields.md,
// and sections D (form sections) and E (option prices) of
// docs/events-types-sessions-contract.md.
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom";
import axios from "axios";
import { shallowEqual, useSelector } from "react-redux";
import { Button, IconButton, Snackbar, TextField } from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import { Modal, Spinner } from "react-bootstrap";
import { SnackbarWrapper } from "../../Snackbar/SnackbarWrapper";
import {
  FIELD_TYPES,
  buildFieldsPutBody,
  copyFieldsFromSource,
  copySectionsFromSource,
  dependencyChoices,
  describeDependency,
  fieldsToEditor,
  formatMoney,
  formSectionOrder,
  groupBySection,
  isActive,
  lockState,
  maxChoicesProblem,
  maxChoicesSupported,
  newEditorField,
  newEditorOption,
  newEditorSection,
  orderBySection,
  priceFromEditor,
  pricesNotAllowedMessage,
  sectionName,
  sectionsToEditor,
  typeHasOptions,
  typeHasPrices,
  validateFieldsDefinition,
  validateSectionsDefinition
} from "./eventFields";

const fieldsUrl = "api/workshops/fields/";
// The server's cap (PUT /fields validation).
const MAX_SECTIONS = 50;
const LOCKED_REASON = "Locked: registrations exist";
const TYPE_REASON = "This event type does not allow registration questions";
const PRICE_LOCKED_REASON = "Locked: registrations exist, so a saved answer's price is fixed";
// The server's 409 wording for lowering a saved question's max choices.
const MAX_CHOICES_LOCKED = "cannot lower the choice limit after registrations exist";

const hasPrice = o => priceFromEditor(o.price_adj) !== 0;
const fieldHasPrices = f =>
  typeHasPrices(f.field_type) && f.options.some(hasPrice);

const move = (list, from, to) => {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item);
  return next;
};

const swap = (list, i, j) => {
  const next = [...list];
  [next[i], next[j]] = [next[j], next[i]];
  return next;
};

// Hide / Show for a question or an answer: the eye icon plus the word, with
// a hover title that still shows when disabled.
const HideShowButton = ({ active, title, disabled, onClick }) => (
  <span title={title}>
    <Button
      size="small"
      disabled={disabled}
      onClick={onClick}
      startIcon={
        active ? (
          <VisibilityOffIcon fontSize="small" />
        ) : (
          <VisibilityIcon fontSize="small" />
        )
      }
      style={{ textTransform: "none", minWidth: 0, whiteSpace: "nowrap" }}
    >
      {active ? "Hide" : "Show"}
    </Button>
  </span>
);

// Small icon button with a hover title that still shows when disabled.
const RowButton = ({ title, disabled, onClick, children }) => (
  <span title={title}>
    <IconButton
      size="small"
      disabled={disabled}
      onClick={onClick}
      aria-label={title}
    >
      {children}
    </IconButton>
  </span>
);

function CopyQuestionsDialog({
  show,
  onHide,
  workshopId,
  branchId,
  authToken,
  locked,
  onCopy,
  onError
}) {
  const [events, setEvents] = useState(null);
  const [source, setSource] = useState(null);
  const [copying, setCopying] = useState(false);

  useEffect(() => {
    if (!show || !branchId) return undefined;
    let cancelled = false;
    setSource(null);
    axios(`api/workshops/workshops/${branchId}`, {
      headers: { "x-auth-token": authToken }
    })
      .then(res => {
        if (cancelled) return;
        setEvents(
          (res.data.results || [])
            .filter(e => String(e.workshop_id) !== String(workshopId))
            .sort((a, b) => b.workshop_id - a.workshop_id)
        );
      })
      .catch(e => {
        console.error("events in branch: ", e);
        if (cancelled) return;
        setEvents([]);
        onError(
          e.response?.data?.message || "Couldn't load this branch's events"
        );
      });
    return () => {
      cancelled = true;
    };
    // onError is a fresh function each render; reload only on these.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, branchId, workshopId, authToken]);

  const copy = async () => {
    setCopying(true);
    try {
      await onCopy(source);
    } finally {
      setCopying(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} aria-labelledby="copy-questions-title">
      <Modal.Header closeButton>
        <Modal.Title id="copy-questions-title">
          Copy questions from another event
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {events ? (
          <Autocomplete
            options={events}
            getOptionLabel={e => `${e.workshop_name} (#${e.workshop_id})`}
            value={source}
            onChange={(_, value) => setSource(value)}
            renderInput={params => (
              <TextField
                {...params}
                label="Event to copy from"
                variant="outlined"
              />
            )}
          />
        ) : (
          <div className="d-flex align-items-center text-muted">
            <Spinner animation="border" size="sm" className="mr-2" />
            Loading this branch's events…
          </div>
        )}
        <small className="form-text text-muted mt-3">
          Its active questions are added and saved straight away, together with
          any unsaved changes here: those in a section come with their section,
          after this event&apos;s sections; those without one join this
          event&apos;s questions without a section, which are asked first.
          {locked &&
            " This event has registrations, so the copies are added as optional questions."}
        </small>
      </Modal.Body>
      <Modal.Footer>
        <button
          type="button"
          onClick={onHide}
          className="btn btn-light btn-elevate"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={copy}
          disabled={!source || copying}
          className="btn btn-primary btn-elevate"
        >
          {copying ? "Copying…" : "Copy questions"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

// readOnly: the event's type does not allow questions; only deactivating
// the question itself (QuestionEditor) is left. prices: { show, editable,
// reason }: whether each answer has a price box (the event's type allows
// option prices, or a saved price is left to clear) and whether it can be
// changed (the type allows prices on the saved event and on the form, and the
// lock rule leaves this answer's price open).
function OptionsEditor({ field, rules, onChange, readOnly, prices }) {
  const setOptions = options => onChange({ ...field, options });
  const updateOption = (j, patch) =>
    setOptions(field.options.map((o, k) => (k === j ? { ...o, ...patch } : o)));
  const isCheckbox = field.field_type === "checkbox";

  return (
    <div className="mt-3">
      <label className="mb-1">
        {isCheckbox ? "Text beside the tick box" : "Answer choices"}
      </label>
      {field.options.map((o, j) => (
        <div
          key={o.workshop_field_option_id}
          className="d-flex align-items-center mb-2"
          style={{ opacity: isActive(o) ? 1 : 0.5 }}
        >
          <input
            type="text"
            className="form-control form-control-sm"
            value={o.label}
            maxLength={200}
            placeholder={
              isCheckbox ? "I accept the tournament rules" : "e.g. U10"
            }
            aria-label={
              isCheckbox ? "Text beside the tick box" : `Answer ${j + 1}`
            }
            disabled={readOnly}
            onChange={e => updateOption(j, { label: e.target.value })}
          />
          {prices.show && (
            <PriceBox
              option={o}
              label={
                isCheckbox ? "Price when ticked" : `Price of answer ${j + 1}`
              }
              editable={prices.editable && rules.canChangePrice(o)}
              reason={
                !prices.editable
                  ? prices.reason
                  : rules.canChangePrice(o)
                  ? undefined
                  : PRICE_LOCKED_REASON
              }
              onChange={price_adj => updateOption(j, { price_adj })}
            />
          )}
          {!isCheckbox && (
            <>
              <RowButton
                title="Move up"
                disabled={readOnly || j === 0}
                onClick={() => setOptions(move(field.options, j, j - 1))}
              >
                <ArrowUpwardIcon fontSize="small" />
              </RowButton>
              <RowButton
                title="Move down"
                disabled={readOnly || j === field.options.length - 1}
                onClick={() => setOptions(move(field.options, j, j + 1))}
              >
                <ArrowDownwardIcon fontSize="small" />
              </RowButton>
            </>
          )}
          <HideShowButton
            active={isActive(o)}
            title={
              isActive(o)
                ? "Deactivate (hidden from new registrations, answers kept)"
                : "Reactivate"
            }
            disabled={readOnly}
            onClick={() => updateOption(j, { active_id: isActive(o) ? 0 : 1 })}
          />
          <RowButton
            title={
              rules.canDeleteOption(o)
                ? "Delete"
                : `${LOCKED_REASON} — deactivate instead`
            }
            disabled={readOnly || !rules.canDeleteOption(o)}
            onClick={() => setOptions(field.options.filter((_, k) => k !== j))}
          >
            <DeleteOutlineIcon fontSize="small" />
          </RowButton>
        </div>
      ))}
      {!readOnly && !(isCheckbox && field.options.some(isActive)) && (
        <button
          type="button"
          className="btn btn-link btn-sm p-0"
          onClick={() => setOptions([...field.options, newEditorOption()])}
        >
          + Add answer
        </button>
      )}
    </div>
  );
}

// An answer's price: added to the participant's fee when chosen (negative
// takes it off). Blank is 0.
const PriceBox = ({ option, label, editable, reason, onChange }) => (
  <div
    className="input-group input-group-sm ml-2 flex-shrink-0"
    style={{ width: 130 }}
    title={reason || "Added to the participant's fee when chosen (negative to take off)"}
  >
    <div className="input-group-prepend">
      <span className="input-group-text">+ $</span>
    </div>
    <input
      type="text"
      inputMode="decimal"
      className="form-control"
      value={option.price_adj}
      placeholder="0"
      maxLength={10}
      aria-label={label}
      disabled={!editable}
      onChange={e => onChange(e.target.value.replace(/[^0-9.-]/g, ""))}
    />
  </div>
);

// A multiple choice's limit on how many answers may be ticked. Blank is no
// limit; once locked, a saved limit can only be raised or cleared.
function MaxChoicesBox({ field, rules, onChange, readOnly }) {
  const id = `question-max-choices-${field._uid}`;
  const problem = rules.canSetMaxChoices(field)
    ? maxChoicesProblem(field)
    : MAX_CHOICES_LOCKED;
  return (
    <div className="mt-3">
      <div className="d-flex align-items-center flex-wrap">
        <label htmlFor={id} className="mb-0 mr-2">
          Max choices
        </label>
        <input
          id={id}
          type="text"
          inputMode="numeric"
          className={`form-control form-control-sm${problem ? " is-invalid" : ""}`}
          style={{ width: 90 }}
          value={field.max_choices}
          maxLength={3}
          placeholder="No limit"
          disabled={readOnly}
          title={readOnly ? TYPE_REASON : undefined}
          onChange={e =>
            onChange({ ...field, max_choices: e.target.value.replace(/[^0-9]/g, "") })
          }
        />
        <small className="text-muted ml-2">
          Leave blank for no limit
          {rules.locked && field.workshop_field_id
            ? ` (${LOCKED_REASON}, so it can't be lowered)`
            : ""}
        </small>
      </div>
      {problem && (
        <div className="invalid-feedback d-block">
          {problem.charAt(0).toUpperCase() + problem.slice(1)}
        </div>
      )}
    </div>
  );
}

// index: the question's place in the whole form (fields, in form order);
// onMoveUp / onMoveDown: within its section, null at either end.
function QuestionEditor({
  field,
  index,
  fields,
  sections,
  rules,
  onChange,
  onMoveUp,
  onMoveDown,
  onSectionChange,
  onDelete,
  readOnly,
  prices,
  withMaxChoices
}) {
  const choices = dependencyChoices(fields, index);
  const requiredDisabled =
    readOnly || (!field.required_ind && !rules.canMakeRequired(field));
  const requiredId = `question-required-${field._uid}`;

  const changeType = field_type => {
    let options = field.options;
    if (typeHasOptions(field_type) && options.length === 0)
      options = [newEditorOption()];
    onChange({
      ...field,
      field_type,
      options,
      max_choices: field_type === "multi_select" ? field.max_choices : ""
    });
  };

  return (
    <div
      className="border rounded p-4 mb-4"
      style={{
        position: "relative",
        background: isActive(field) ? "#fff" : "#f3f6f9",
        opacity: isActive(field) ? 1 : 0.7
      }}
    >
      {/* Move / Hide / Delete in the card's top-right corner, on the
          labels' line, so the type picker has room for its labels. Above
          the grid columns (position: relative), which would otherwise
          cover them and take their clicks. */}
      <div
        className="d-flex align-items-center"
        style={{ position: "absolute", top: 8, right: 12, zIndex: 1 }}
      >
        <RowButton
          title="Move up"
          disabled={readOnly || !onMoveUp}
          onClick={onMoveUp}
        >
          <ArrowUpwardIcon fontSize="small" />
        </RowButton>
        <RowButton
          title="Move down"
          disabled={readOnly || !onMoveDown}
          onClick={onMoveDown}
        >
          <ArrowDownwardIcon fontSize="small" />
        </RowButton>
        <HideShowButton
          active={isActive(field)}
          title={
            isActive(field)
              ? "Deactivate (hidden from new registrations, answers kept)"
              : readOnly
              ? TYPE_REASON
              : rules.reactivatesAsOptional(field)
              ? `Reactivate as optional (${LOCKED_REASON})`
              : "Reactivate"
          }
          disabled={readOnly && !isActive(field)}
          onClick={() =>
            onChange(
              isActive(field)
                ? { ...field, active_id: 0 }
                : {
                    ...field,
                    active_id: 1,
                    required_ind: rules.reactivatesAsOptional(field)
                      ? 0
                      : field.required_ind
                  }
            )
          }
        />
        <RowButton
          title={
            rules.canDeleteField(field)
              ? "Delete"
              : `${LOCKED_REASON} — deactivate instead`
          }
          // Allowed in read-only mode too (where the lock rule allows
          // it): an unsaved or unanswered question needn't be kept as an
          // inactive row just to clear the event.
          disabled={!rules.canDeleteField(field)}
          onClick={onDelete}
        >
          <DeleteOutlineIcon fontSize="small" />
        </RowButton>
      </div>
      <div className="form-group row mb-0">
        <div className="col-lg-5">
          <label className="required-field">
            Question {index + 1} of {fields.length}
            {!isActive(field) && (
              <span className="badge badge-secondary ml-2">Inactive</span>
            )}
          </label>
          <input
            type="text"
            className="form-control"
            value={field.label}
            maxLength={200}
            placeholder="e.g. CFC ID"
            disabled={readOnly}
            onChange={e => onChange({ ...field, label: e.target.value })}
          />
        </div>
        <div className="col-lg-5">
          <label>Answer type</label>
          <select
            className="form-control"
            value={field.field_type}
            disabled={readOnly || !rules.canChangeType(field)}
            title={
              readOnly
                ? TYPE_REASON
                : rules.canChangeType(field)
                ? undefined
                : LOCKED_REASON
            }
            onChange={e => changeType(e.target.value)}
          >
            {FIELD_TYPES.map(t => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-lg-2 d-flex align-items-end">
          <div
            className="d-flex align-items-center mb-3"
            title={
              readOnly
                ? TYPE_REASON
                : requiredDisabled
                ? LOCKED_REASON
                : undefined
            }
          >
            <label className="checkbox checkbox-lg mb-0">
              <input
                id={requiredId}
                type="checkbox"
                checked={!!field.required_ind}
                disabled={requiredDisabled}
                onChange={e =>
                  onChange({ ...field, required_ind: e.target.checked ? 1 : 0 })
                }
              />
              <span></span>
            </label>
            <label
              htmlFor={requiredId}
              className="ml-2 mb-0"
              style={{ cursor: "pointer" }}
            >
              Required
            </label>
          </div>
        </div>
      </div>

      <div className="mt-3">
        <label htmlFor={`question-help-${field._uid}`} className="mb-1">
          Help text (optional)
        </label>
        <input
          id={`question-help-${field._uid}`}
          type="text"
          className="form-control form-control-sm"
          value={field.help_text}
          maxLength={1000}
          placeholder="Shown under the question"
          disabled={readOnly}
          onChange={e => onChange({ ...field, help_text: e.target.value })}
        />
        <small className="form-text text-muted">
          Links: paste the full address, or write [text](https://…). HTML is
          shown as typed.
        </small>
      </div>

      {typeHasOptions(field.field_type) && (
        <OptionsEditor
          field={field}
          rules={rules}
          onChange={onChange}
          readOnly={readOnly}
          prices={prices}
        />
      )}

      {withMaxChoices && field.field_type === "multi_select" && (
        <MaxChoicesBox
          field={field}
          rules={rules}
          onChange={onChange}
          readOnly={readOnly}
        />
      )}

      {(choices.length > 0 || field.depends_on_option_id != null) && (
        <div className="mt-3 d-flex align-items-center flex-wrap">
          <label className="mb-0 mr-2">Show</label>
          <select
            className="form-control form-control-sm"
            style={{ maxWidth: 420 }}
            value={field.depends_on_option_id ?? ""}
            disabled={readOnly || !rules.canChangeDependency(field)}
            title={
              readOnly
                ? TYPE_REASON
                : rules.canChangeDependency(field)
                ? undefined
                : LOCKED_REASON
            }
            onChange={e =>
              onChange({
                ...field,
                depends_on_option_id:
                  e.target.value === "" ? null : Number(e.target.value)
              })
            }
          >
            <option value="">always</option>
            {choices.map(c => (
              <optgroup
                key={c.field._uid}
                label={`only when "${c.field.label.trim() || "(untitled)"}" is`}
              >
                {c.options.map(o => (
                  <option
                    key={o.workshop_field_option_id}
                    value={o.workshop_field_option_id}
                  >
                    {o.label}
                  </option>
                ))}
              </optgroup>
            ))}
            {field.depends_on_option_id != null &&
              !choices.some(c =>
                c.options.some(
                  o => o.workshop_field_option_id === field.depends_on_option_id
                )
              ) && (
                // A saved rule the picker would not offer now (its answer was
                // deactivated, or the question moved): still valid, so shown.
                <option value={field.depends_on_option_id}>
                  {describeDependency(fields, field.depends_on_option_id)}
                </option>
              )}
          </select>
        </div>
      )}

      {sections && sections.length > 0 && (
        <div className="mt-3 d-flex align-items-center flex-wrap">
          <label
            htmlFor={`question-section-${field._uid}`}
            className="mb-0 mr-2"
          >
            Section
          </label>
          <select
            id={`question-section-${field._uid}`}
            className="form-control form-control-sm"
            style={{ maxWidth: 420 }}
            value={field.workshop_field_section_id ?? ""}
            disabled={readOnly}
            title={readOnly ? TYPE_REASON : undefined}
            onChange={e =>
              onSectionChange(
                e.target.value === "" ? null : Number(e.target.value)
              )
            }
          >
            <option value="">No section (asked first)</option>
            {sections.map((s, i) => (
              <option
                key={s.workshop_field_section_id}
                value={s.workshop_field_section_id}
              >
                {sectionName(s, i)}
                {isActive(s) ? "" : " (hidden)"}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
}

// A section's header row: its title and description, order, Hide / Show and
// Delete. Wording and order stay editable once registrations exist; a section
// that still holds questions cannot be deleted (the server refuses with a
// 400), an empty one always can.
function SectionEditor({
  section,
  index,
  count,
  questionCount,
  onChange,
  onMove,
  onDelete,
  readOnly,
  busy
}) {
  const titleId = `section-title-${section.workshop_field_section_id}`;
  // A hidden section is shown where its questions are asked (after the
  // questions without a section); it moves only once shown again.
  const hidden = !isActive(section);
  const descriptionId = `section-description-${section.workshop_field_section_id}`;
  return (
    <div
      className="rounded p-4 mb-4"
      style={{
        position: "relative",
        background: "#eef3fc",
        borderLeft: "4px solid #3699ff",
        opacity: isActive(section) ? 1 : 0.7
      }}
    >
      <div
        className="d-flex align-items-center"
        style={{ position: "absolute", top: 8, right: 12, zIndex: 1 }}
      >
        <RowButton
          title={hidden ? "Show the section to move it" : "Move section up"}
          disabled={readOnly || hidden || index === 0}
          onClick={() => onMove(index - 1)}
        >
          <ArrowUpwardIcon fontSize="small" />
        </RowButton>
        <RowButton
          title={hidden ? "Show the section to move it" : "Move section down"}
          disabled={readOnly || hidden || index === count - 1}
          onClick={() => onMove(index + 1)}
        >
          <ArrowDownwardIcon fontSize="small" />
        </RowButton>
        <HideShowButton
          active={isActive(section)}
          title={
            isActive(section)
              ? "Hide the heading: its questions are then asked without one, after the questions with no section"
              : "Show the heading again, with its questions back in its place"
          }
          disabled={busy}
          onClick={() =>
            onChange({ ...section, active_id: isActive(section) ? 0 : 1 })
          }
        />
        <RowButton
          title={
            questionCount === 0
              ? "Delete section"
              : "Move its questions to another section or to No section first"
          }
          disabled={busy || questionCount > 0}
          onClick={onDelete}
        >
          <DeleteOutlineIcon fontSize="small" />
        </RowButton>
      </div>
      <label htmlFor={titleId} className="required-field">
        Section {index + 1} of {count}
        {hidden && <span className="badge badge-secondary ml-2">Hidden</span>}
        <span className="text-muted ml-2">
          {questionCount === 0
            ? "no questions yet"
            : `${questionCount} question${questionCount === 1 ? "" : "s"}`}
        </span>
      </label>
      <input
        id={titleId}
        type="text"
        className="form-control"
        style={{ maxWidth: 640 }}
        value={section.title}
        maxLength={200}
        placeholder="e.g. Player details"
        disabled={readOnly}
        onChange={e => onChange({ ...section, title: e.target.value })}
      />
      <label htmlFor={descriptionId} className="mt-3 mb-1">
        Description (optional)
      </label>
      <textarea
        id={descriptionId}
        className="form-control form-control-sm"
        style={{ maxWidth: 640 }}
        rows={2}
        value={section.description}
        maxLength={2000}
        placeholder="Shown under the heading, above its questions"
        disabled={readOnly}
        onChange={e => onChange({ ...section, description: e.target.value })}
      />
      {hidden && (
        <small className="form-text text-muted">
          Hidden: the questions below are asked here, after the questions with
          no section, without this heading.
        </small>
      )}
    </div>
  );
}

// selectedType: the event type chosen on the form (may be unsaved), from
// GET /api/workshops/event-types; undefined while those load. Whether the
// SAVED event may have questions comes from GET /fields (questions_allowed).
// onSummary: called with { status, questionsAllowed, hasActiveSaved, dirty }
// whenever those change, so the Edit Event page can decide whether its
// Registration questions tab is shown (this stays mounted, hidden, while
// Basic info is open). savedType: the saved event's type row and eventFee
// its saved fee (the server checks option prices against both); the fee in
// GET /fields (event_fee) wins when the server sends it.
export function EventQuestionsEditor({
  workshopId,
  selectedType,
  savedType,
  eventFee,
  onSummary
}) {
  const authToken = useSelector(state => state.auth.authToken, shallowEqual);
  const branch = useSelector(
    state => state.auth.branches.currentBranch,
    shallowEqual
  );
  const headers = { "x-auth-token": authToken };

  // status: "loading" | "error" | "loaded"
  const [status, setStatus] = useState("loading");
  const [loadError, setLoadError] = useState("");
  const [reload, setReload] = useState(0);
  const [stored, setStored] = useState({
    locked: false,
    questionsAllowed: true,
    pricesAllowed: false,
    fields: []
  });
  const [fields, setFields] = useState([]);
  // Form sections (V102), in order; sectionsSupported is false for a server
  // that sends none, which then gets the body it always did.
  const [sections, setSections] = useState([]);
  const [sectionsSupported, setSectionsSupported] = useState(false);
  // A server that knows max_choices (maxChoicesSupported); otherwise the box
  // is hidden and the body leaves it out.
  const [withMaxChoices, setWithMaxChoices] = useState(false);
  const [savedBody, setSavedBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [problems, setProblems] = useState([]);
  const [copyOpen, setCopyOpen] = useState(false);
  const [toast, setToast] = useState(null); // { variant, message }

  const applyResults = results => {
    const next = {
      locked: !!results?.locked,
      // A server without event types (V097) sends no flag: allowed.
      questionsAllowed: results?.questions_allowed !== false,
      // A server without priced options (V103) sends no flag: none.
      pricesAllowed: results?.priced_options_allowed === true,
      // A server that sends event_fee sends null for an event with no fee
      // stored, which it prices as 0; without the key the fee is unknown.
      eventFee:
        results && "event_fee" in results ? Number(results.event_fee) || 0 : null,
      fields: results?.fields || []
    };
    const supported = Array.isArray(results?.sections);
    const maxChoicesOn = maxChoicesSupported(next.fields);
    const editorSections = sectionsToEditor(results?.sections);
    const editorFields = fieldsToEditor(
      next.fields,
      formSectionOrder(editorSections)
    );
    setStored(next);
    setSectionsSupported(supported);
    setWithMaxChoices(maxChoicesOn);
    setSections(editorSections);
    setFields(editorFields);
    setSavedBody(
      JSON.stringify(
        buildFieldsPutBody(
          editorFields,
          supported ? editorSections : undefined,
          { withMaxChoices: maxChoicesOn }
        )
      )
    );
    setProblems([]);
  };
  const bodySections = list => (sectionsSupported ? list : undefined);

  useEffect(() => {
    if (!workshopId) return undefined;
    let cancelled = false;
    setStatus("loading");
    axios(fieldsUrl + workshopId, { headers })
      .then(res => {
        if (cancelled) return;
        applyResults(res.data.results);
        setStatus("loaded");
      })
      .catch(e => {
        console.error("registration questions: ", e);
        if (cancelled) return;
        // The server's reason (e.g. a 403) so it isn't taken for an outage.
        setLoadError(e.response?.data?.message || "");
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workshopId, authToken, reload]);

  const rules = useMemo(() => lockState(stored.fields, stored.locked), [
    stored
  ]);

  // What the section shows, from the type chosen on the form and the saved
  // event's own type (questions_allowed):
  //  "edit"          both allow questions: the builder.
  //  "saveTypeFirst" the chosen type allows them but the saved one does not:
  //                  the server would refuse a save until the event is saved
  //                  with the new type.
  //  "readOnly"      the chosen type does not allow them but active ones are
  //                  saved: staff can only deactivate them and save (the
  //                  server accepts a definition with no active questions).
  //  "hidden"        the chosen type does not allow them and none are active.
  const selectedAllows = selectedType
    ? Number(selectedType.custom_fields_ind) === 1
    : stored.questionsAllowed;
  const hasActiveSaved = stored.fields.some(isActive);
  const mode = selectedAllows
    ? stored.questionsAllowed
      ? "edit"
      : "saveTypeFirst"
    : hasActiveSaved
    ? "readOnly"
    : "hidden";
  const readOnly = mode === "readOnly";

  // When the SAVED type forbids questions, the server only accepts a
  // definition with none active, so a partial deactivation would be refused
  // (and reloaded away): Save waits until every question is inactive. When
  // only the form's unsaved choice forbids them, any save is accepted.
  const saveBlocked =
    readOnly && !stored.questionsAllowed && fields.some(isActive);
  // The fee the server checks prices against (no fee stored counts as 0),
  // or null while unknown (an older server, and the event not loaded).
  const savedFee =
    stored.eventFee != null
      ? stored.eventFee
      : eventFee === undefined
      ? null
      : Number(eventFee) || 0;

  // Option prices (V103), from the type chosen on the form and the saved
  // event's (priced_options_allowed), as for questions above. They can be
  // set only when both allow them; otherwise a saved non-zero price is shown
  // read-only, with why, and "Set prices to 0" (the server refuses a type
  // change while any remain, and a save under a saved type without prices).
  const selectedAllowsPrices = selectedType
    ? Number(selectedType.priced_options_ind) === 1
    : stored.pricesAllowed;
  const pricesEditable =
    mode === "edit" && stored.pricesAllowed && selectedAllowsPrices;
  const pricedFields = fields.filter(fieldHasPrices);
  const pricesNotice =
    pricedFields.length === 0 || pricesEditable
      ? null
      : !stored.pricesAllowed
      ? `${pricesNotAllowedMessage(savedType?.event_type_name)}: set them to 0, then save.`
      : !selectedAllowsPrices
      ? `${pricesNotAllowedMessage(selectedType?.event_type_name)}. Set this event's option prices to 0 before changing its type.`
      : null;
  const prices = {
    show: pricesEditable || pricedFields.length > 0,
    editable: pricesEditable,
    reason: pricesNotice ? "Use Set prices to 0" : TYPE_REASON
  };
  // Clears every price the lock rule still lets change.
  const clearPrices = () =>
    setFields(
      fields.map(f =>
        fieldHasPrices(f)
          ? {
              ...f,
              options: f.options.map(o =>
                rules.canChangePrice(o) ? { ...o, price_adj: "" } : o
              )
            }
          : f
      )
    );
  const canClearPrices = pricedFields.some(f =>
    f.options.some(o => hasPrice(o) && rules.canChangePrice(o))
  );
  const hasFrozenPrices = pricedFields.some(f =>
    f.options.some(o => hasPrice(o) && !rules.canChangePrice(o))
  );
  const dirty =
    status === "loaded" &&
    JSON.stringify(
      buildFieldsPutBody(fields, bodySections(sections), { withMaxChoices })
    ) !==
      savedBody;

  useEffect(() => {
    if (onSummary)
      onSummary({
        status,
        questionsAllowed: stored.questionsAllowed,
        hasActiveSaved,
        dirty
      });
  }, [onSummary, status, stored.questionsAllowed, hasActiveSaved, dirty]);

  const updateField = (i, next) =>
    setFields(fields.map((f, k) => (k === i ? next : f)));

  // fields stay in form order (orderBySection), so a question's position in
  // the list is also its place in the form and the PUT's order_num.
  // Hidden sections come first (formSectionOrder), where their questions are
  // asked.
  const groups = groupBySection(fields, formSectionOrder(sections));

  // Into another section (or none): to the end of it.
  const moveToSection = (i, sectionId) =>
    setFields(
      orderBySection(
        [
          ...fields.filter((_, k) => k !== i),
          { ...fields[i], workshop_field_section_id: sectionId }
        ],
        formSectionOrder(sections)
      )
    );

  const addQuestion = (sectionId = null) =>
    setFields(
      orderBySection(
        [
          ...fields,
          { ...newEditorField(), workshop_field_section_id: sectionId }
        ],
        formSectionOrder(sections)
      )
    );

  // Hiding or showing a section moves its questions to where they are asked.
  const updateSection = (j, nextSection) => {
    const next = sections.map((s, k) => (k === j ? nextSection : s));
    setSections(next);
    if (isActive(nextSection) !== isActive(sections[j]))
      setFields(orderBySection(fields, formSectionOrder(next)));
  };

  const moveSection = (j, to) => {
    const next = move(sections, j, to);
    setSections(next);
    setFields(orderBySection(fields, formSectionOrder(next)));
  };

  const loadSaved = async () => {
    const res = await axios(fieldsUrl + workshopId, { headers });
    applyResults(res.data.results);
  };

  // One PUT of the whole definition. A 409 (lock rule) or 400 names the
  // question in its message, shown as is. A 409 can mean registrations
  // arrived after this page loaded, and a 404 / 400 about a section "not on
  // this event" that another tab deleted it, so in those cases the saved
  // definition and its lock are loaded again (this discards the edits the
  // server refused; a retry would only fail the same way).
  const save = async (toSave, successMessage, toSaveSections = sections) => {
    const errors = [
      ...(sectionsSupported ? validateSectionsDefinition(toSaveSections) : []),
      // The server goes by the saved type and fee.
      ...validateFieldsDefinition(toSave, {
        allowed: stored.pricesAllowed,
        typeName: savedType?.event_type_name,
        fee: savedFee
      }),
      // The lock rule the server refuses with a 409.
      ...(withMaxChoices
        ? toSave
            .filter(f => !rules.canSetMaxChoices(f))
            .map(
              f =>
                `${f.label.trim() || "(untitled)"}: ${MAX_CHOICES_LOCKED}`
            )
        : [])
    ];
    setProblems(errors);
    if (errors.length) {
      setToast({
        variant: "error",
        message: "Fix the problems listed below, then save again"
      });
      return false;
    }
    setSaving(true);
    try {
      const res = await axios.put(
        fieldsUrl + workshopId,
        buildFieldsPutBody(toSave, bodySections(toSaveSections), {
          withMaxChoices
        }),
        { headers }
      );
      applyResults(res.data.results);
      setToast({ variant: "success", message: successMessage });
      return true;
    } catch (e) {
      console.error("save registration questions: ", e);
      let message =
        e.response?.data?.message || "Couldn't save the registration questions";
      const stale =
        e.response?.status === 409 ||
        ([400, 404].includes(e.response?.status) &&
          /not on this event/i.test(e.response?.data?.message || ""));
      if (stale) {
        try {
          await loadSaved();
          message +=
            ". The saved questions were loaded again — make your change from there.";
        } catch (reloadError) {
          console.error("reload registration questions: ", reloadError);
        }
      }
      setToast({ variant: "error", message });
      return false;
    } finally {
      setSaving(false);
    }
  };

  const copyFrom = async source => {
    let sourceFields;
    let sourceSections;
    try {
      const res = await axios(fieldsUrl + source.workshop_id, { headers });
      sourceFields = res.data.results?.fields || [];
      sourceSections = sectionsSupported ? res.data.results?.sections : [];
    } catch (e) {
      console.error("copy questions: ", e);
      setToast({
        variant: "error",
        message:
          e.response?.data?.message || "Couldn't load that event's questions"
      });
      return;
    }
    // Keys of questions removed here but still on the server stay taken (the
    // server deactivates a question with answers instead of deleting it).
    // The source's sections come along with the questions in them, after
    // this event's sections.
    const { sections: copiedSections, sectionIdMap } = copySectionsFromSource(
      sourceSections
    );
    const copied = copyFieldsFromSource(
      sourceFields,
      [...fields, ...stored.fields].map(f => f.field_key),
      { forceOptional: stored.locked, sectionIdMap, copyPrices: pricesEditable }
    );
    const nextSections = [
      ...sections,
      ...copiedSections.filter(s =>
        copied.some(
          f => f.workshop_field_section_id === s.workshop_field_section_id
        )
      )
    ];
    if (copied.length === 0) {
      setToast({
        variant: "warning",
        message: `${source.workshop_name} has no active questions to copy`
      });
      return;
    }
    if (nextSections.length > MAX_SECTIONS) {
      setToast({
        variant: "error",
        message: `This would give the event ${nextSections.length} sections; an event can have at most ${MAX_SECTIONS}`
      });
      return;
    }
    const ok = await save(
      orderBySection([...fields, ...copied], formSectionOrder(nextSections)),
      `Copied ${copied.length} question${copied.length === 1 ? "" : "s"} from ${
        source.workshop_name
      }`,
      nextSections
    );
    if (ok) setCopyOpen(false);
  };

  // In a portal: this editor sits in a tab that is display:none while Basic
  // info is open, and a toast that fires then (a slow save) must still show.
  const toastView = ReactDOM.createPortal(
    <Snackbar
      anchorOrigin={{ vertical: "top", horizontal: "right" }}
      open={!!toast}
      // A refusal's explanation lives only in this toast, so it stays
      // until closed, and a click elsewhere on the page does not close it.
      autoHideDuration={toast?.variant === "success" ? 3000 : null}
      onClose={(e, reason) => reason !== "clickaway" && setToast(null)}
    >
      <SnackbarWrapper
        onClose={() => setToast(null)}
        variant={toast?.variant || "success"}
        message={toast?.message || ""}
      />
    </Snackbar>,
    document.body
  );

  return (
    <div>
      {toastView}

      <div className="d-flex align-items-start justify-content-between flex-wrap mb-2">
        <p className="text-muted mb-2 mr-4" style={{ maxWidth: 640 }}>
          Asked for each participant when they register for this event on the
          website, and when staff add a student from Family Profile. Saved here
          with Save questions, separately from Basic info.
        </p>
        {status === "loaded" && (mode === "edit" || readOnly) && (
          <div className="mb-2">
            {mode === "edit" && (
              <>
                <button
                  type="button"
                  className="btn btn-light btn-sm mr-2"
                  disabled={saving}
                  onClick={() => setCopyOpen(true)}
                >
                  Copy questions from another event
                </button>
                {sectionsSupported && (
                  <span
                    title={
                      sections.length >= MAX_SECTIONS
                        ? `An event can have at most ${MAX_SECTIONS} sections`
                        : undefined
                    }
                  >
                    <button
                      type="button"
                      className="btn btn-light-primary btn-sm mr-2"
                      disabled={saving || sections.length >= MAX_SECTIONS}
                      onClick={() =>
                        setSections([...sections, newEditorSection()])
                      }
                    >
                      + Add section
                    </button>
                  </span>
                )}
                <button
                  type="button"
                  className="btn btn-light-primary btn-sm mr-2"
                  disabled={saving}
                  onClick={() => addQuestion()}
                >
                  + Add question
                </button>
              </>
            )}
            {saveBlocked && (
              <span className="text-muted mr-2">
                Deactivate every question, then save
              </span>
            )}
            <button
              type="button"
              className="btn btn-primary btn-sm"
              disabled={!dirty || saving || saveBlocked}
              onClick={() => save(fields, "Saved the registration questions")}
            >
              {saving ? "Saving…" : "Save questions"}
            </button>
          </div>
        )}
      </div>

      {status === "loading" && (
        <div className="d-flex align-items-center text-muted py-3">
          <Spinner animation="border" size="sm" className="mr-2" />
          Loading registration questions…
        </div>
      )}
      {status === "error" && (
        <p className="text-danger">
          Couldn't load the registration questions
          {loadError ? `: ${loadError}` : ""}.{" "}
          <button
            type="button"
            className="btn btn-link p-0 align-baseline"
            onClick={() => setReload(n => n + 1)}
          >
            Try again
          </button>
        </p>
      )}

      {status === "loaded" && mode === "hidden" && (
        <p className="text-muted">
          {TYPE_REASON}, and this event has no active questions.
        </p>
      )}

      {status === "loaded" && (mode === "edit" || readOnly) && (
        <>
          {readOnly && (
            <div className="alert alert-custom alert-light-warning mb-4">
              <div className="alert-text">
                {TYPE_REASON}. Deactivate them, then save.
              </div>
            </div>
          )}
          {!readOnly && stored.locked && (
            <div className="alert alert-custom alert-light-warning mb-4">
              <div className="alert-text">
                <b>{LOCKED_REASON}.</b> You can still add optional questions and
                answers, edit wording and order, add and edit sections and move
                questions between them, make a question optional, and deactivate
                questions or answers (a deactivated question comes back as
                optional). Deleting, changing a type, a &quot;Show&quot; rule
                or a saved answer&apos;s price, or making a question required is
                no longer possible — create a new event for a different form.
              </div>
            </div>
          )}
          {pricesNotice && (
            <div className="alert alert-custom alert-light-warning mb-4">
              <div className="alert-text">
                {pricesNotice}
                {hasFrozenPrices &&
                  " Prices on saved answers are fixed now that registrations exist, so this event keeps a type with option prices."}
                {canClearPrices && (
                  <button
                    type="button"
                    className="btn btn-light-warning btn-sm ml-3"
                    disabled={saving}
                    onClick={clearPrices}
                  >
                    Set prices to 0
                  </button>
                )}
              </div>
            </div>
          )}
          {mode === "edit" &&
            !stored.pricesAllowed &&
            selectedAllowsPrices &&
            fields.some(f => typeHasPrices(f.field_type)) && (
              <p className="text-muted">
                Save the event with its new type on Basic info to set prices
                on answers.
              </p>
            )}
          {prices.show && pricesEditable && (
            <p className="text-muted">
              A price on an answer is added to the participant&apos;s fee
              {savedFee != null
                ? ` ($${formatMoney(savedFee)})`
                : ""}{" "}
              when it is chosen; a negative price takes it off. Leave it blank
              for no change.
            </p>
          )}
          {problems.length > 0 && (
            <div className="alert alert-custom alert-light-danger mb-4">
              <div className="alert-text">
                <ul className="mb-0 pl-4">
                  {problems.map(p => (
                    <li key={p}>{p}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
          {fields.length === 0 && sections.length === 0 ? (
            <p className="text-muted">
              No questions — the website registers for this event exactly as
              before.
            </p>
          ) : (
            groups.map(group => {
              const sectionIndex = group.section
                ? sections.indexOf(group.section)
                : -1;
              return (
                <div
                  key={
                    group.section
                      ? group.section.workshop_field_section_id
                      : "none"
                  }
                >
                  {group.section && (
                    <SectionEditor
                      section={group.section}
                      index={sectionIndex}
                      count={sections.length}
                      questionCount={group.fields.length}
                      onChange={next => updateSection(sectionIndex, next)}
                      onMove={to => moveSection(sectionIndex, to)}
                      onDelete={() =>
                        setSections(
                          sections.filter((_, k) => k !== sectionIndex)
                        )
                      }
                      readOnly={readOnly}
                      busy={saving}
                    />
                  )}
                  <div className={group.section ? "pl-6" : undefined}>
                    {group.fields.map(({ field, index: i }, k) => (
                      <QuestionEditor
                        key={field._uid}
                        field={field}
                        index={i}
                        fields={fields}
                        sections={sectionsSupported ? sections : null}
                        rules={rules}
                        onChange={next => updateField(i, next)}
                        onMoveUp={
                          k > 0
                            ? () =>
                                setFields(
                                  swap(fields, i, group.fields[k - 1].index)
                                )
                            : null
                        }
                        onMoveDown={
                          k < group.fields.length - 1
                            ? () =>
                                setFields(
                                  swap(fields, i, group.fields[k + 1].index)
                                )
                            : null
                        }
                        onSectionChange={sectionId =>
                          moveToSection(i, sectionId)
                        }
                        onDelete={() =>
                          setFields(fields.filter((_, k2) => k2 !== i))
                        }
                        readOnly={readOnly}
                        withMaxChoices={withMaxChoices}
                        prices={prices}
                      />
                    ))}
                    {group.section && mode === "edit" && (
                      <button
                        type="button"
                        className="btn btn-link btn-sm p-0 mb-4"
                        disabled={saving}
                        onClick={() =>
                          addQuestion(group.section.workshop_field_section_id)
                        }
                      >
                        + Add question to this section
                      </button>
                    )}
                  </div>
                </div>
              );
            })
          )}
          {dirty && (
            <p className="text-warning mb-0">
              Unsaved changes to the questions.
            </p>
          )}
        </>
      )}

      <CopyQuestionsDialog
        show={copyOpen}
        onHide={() => setCopyOpen(false)}
        workshopId={workshopId}
        branchId={branch?.branch_id}
        authToken={authToken}
        locked={stored.locked}
        onCopy={copyFrom}
        onError={message => setToast({ variant: "error", message })}
      />
    </div>
  );
}
